import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui';
import { useAuth } from '../../context/useAuth';
import { fetchWorkflowHistory, fetchWorkflowById } from '../../api/workflowsApi';
import { fromWorkflowGraph, statusKey } from '../tasks/taskGraph';
import { copyToCanvas } from '../tasks/canvasCopy';
import RunRow from './RunRow';
import { fetchCreditsByRun } from './runCredits';

const PAGE = 25;
const FILTERS = [
  { label: 'All', status: undefined },
  { label: 'Running', status: 'executing' },
  { label: 'Done', status: 'completed' },
  { label: 'Failed', status: 'failed' },
];

const titleOf = (text) => {
  const line = String(text || '').split('\n')[0].trim();
  return line.length > 90 ? `${line.slice(0, 90)}…` : line;
};

const toRun = (w) => ({
  id: w.workflow_id,
  title: titleOf(w.task_description),
  status: statusKey(w.status),
  at: w.created_at,
  steps: w.total_nodes,
});

export function RunsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = user?.organisation_id ? String(user.organisation_id) : undefined;
  const [filter, setFilter] = useState(0);
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [credits, setCredits] = useState(null);
  const token = useRef(0);
  const runsRef = useRef([]);

  const load = useCallback(async (status, offset) => {
    const mine = offset === 0 ? ++token.current : token.current;
    setError(false);
    setLoading(true);
    try {
      const res = await fetchWorkflowHistory({
        limit: PAGE, offset, status, sort_by: 'created_at', sort_order: 'desc',
      });
      if (mine !== token.current) return;
      const next = [...(offset === 0 ? [] : runsRef.current), ...(res.workflows || []).map(toRun)];
      runsRef.current = next;
      setRuns(next);
      setTotal(res.total_count || 0);
      setLoading(false);
      const oldest = next.map((r) => r.at).filter(Boolean).sort()[0];
      const found = await fetchCreditsByRun({ orgId, from: oldest, to: new Date().toISOString() });
      if (mine === token.current) setCredits(found);
    } catch {
      if (mine !== token.current) return;
      setError(true);
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    runsRef.current = [];
    setRuns([]);
    setCredits(null);
    load(FILTERS[filter].status, 0);
  }, [filter, load]);

  const copy = (run) => async () => {
    const detail = await fetchWorkflowById(run.id);
    const flowId = await copyToCanvas({
      name: run.title,
      graph: fromWorkflowGraph(detail.graph),
      source: { kind: 'run', workflowId: run.id },
    });
    navigate(`/canvas?flow=${encodeURIComponent(flowId)}`);
  };

  const retry = () => load(FILTERS[filter].status, runs.length);

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-6xl mx-auto px-6 py-9 flex flex-col gap-6">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Runs</h1>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f, i) => (
            <Button
              key={f.label}
              size="sm"
              variant={i === filter ? 'primary' : 'secondary'}
              aria-pressed={i === filter}
              onClick={() => setFilter(i)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {runs.length > 0 && (
          <ul className="flex flex-col gap-3">
            {runs.map((r) => (
              <RunRow key={r.id} run={r} credits={credits ? credits[r.id] : undefined} onCopy={copy(r)} />
            ))}
          </ul>
        )}

        {error ? (
          <div className="flex items-center gap-3">
            <p role="alert" className="text-status-failed">Couldn't load runs.</p>
            <Button variant="secondary" size="sm" onClick={retry}>Try again</Button>
          </div>
        ) : !loading && runs.length === 0 ? (
          <p className="text-pencil">No runs yet.</p>
        ) : !loading && runs.length < total ? (
          <div>
            <Button variant="secondary" size="sm" onClick={() => load(FILTERS[filter].status, runs.length)}>
              Show more
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Element for /runs: old ?workflow= links open the run at /runs/open. */
export function RunsRoute() {
  const { search } = useLocation();
  if (new URLSearchParams(search).has('workflow')) {
    return <Navigate to={{ pathname: '/runs/open', search }} replace />;
  }
  return <RunsPage />;
}

export default RunsPage;
